import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BandTable = ({ bands, onSelectBand, selectedBand, onEditBand, onDeleteBand }) => {
  const { t } = useTranslation();
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedBands = [...bands]?.sort((a, b) => {
    let aValue = a?.[sortField];
    let bValue = b?.[sortField];
    
    if (sortField === 'minSalary' || sortField === 'maxSalary' || sortField === 'employeeCount') {
      aValue = Number(aValue);
      bValue = Number(bValue);
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: currency || 'EUR'
    })?.format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-success text-success-foreground', label: 'Active' },
      draft: { color: 'bg-warning text-warning-foreground', label: 'Draft' },
      archived: { color: 'bg-muted text-muted-foreground', label: 'Archived' },
      pending: { color: 'bg-primary text-primary-foreground', label: 'Pending' }
    };
    
    const config = statusConfig?.[status] || statusConfig?.draft;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return (
      <Icon 
        name={sortDirection === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
        size={14} 
        className="text-primary" 
      />
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-2 hover:text-foreground transition-smooth"
                >
                  <span>{t('payBandsPage.table2.colName')}</span>
                  <SortIcon field="name" />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('department')}
                  className="flex items-center space-x-2 hover:text-foreground transition-smooth"
                >
                  <span>{t('payBandsPage.table2.colDepartment')}</span>
                  <SortIcon field="department" />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('minSalary')}
                  className="flex items-center space-x-2 hover:text-foreground transition-smooth"
                >
                  <span>{t('payBandsPage.table2.colSalaryRange')}</span>
                  <SortIcon field="minSalary" />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('employeeCount')}
                  className="flex items-center space-x-2 hover:text-foreground transition-smooth"
                >
                  <span>{t('payBandsPage.table2.colEmployees')}</span>
                  <SortIcon field="employeeCount" />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('effectiveDate')}
                  className="flex items-center space-x-2 hover:text-foreground transition-smooth"
                >
                  <span>{t('payBandsPage.table2.colEffectiveDate')}</span>
                  <SortIcon field="effectiveDate" />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">{t('payBandsPage.table2.colStatus')}</th>
              <th className="text-right p-4 font-medium text-muted-foreground">{t('payBandsPage.table2.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedBands?.map((band) => (
              <tr 
                key={band?.id}
                className={`border-t border-border hover:bg-muted/50 cursor-pointer transition-smooth ${
                  selectedBand?.id === band?.id ? 'bg-primary/5 border-primary/20' : ''
                }`}
                onClick={() => onSelectBand(band)}
              >
                <td className="p-4">
                  <div>
                    <div className="font-medium text-foreground">{band?.name}</div>
                    <div className="text-sm text-muted-foreground">{band?.jobLevel}</div>
                  </div>
                </td>
                <td className="p-4 text-foreground">{band?.department}</td>
                <td className="p-4">
                  <div className="text-foreground">
                    {formatCurrency(band?.minSalary, band?.currency)} - {formatCurrency(band?.maxSalary, band?.currency)}
                  </div>
                  <div className="text-sm text-muted-foreground">{band?.currency}</div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="Users" size={16} className="text-muted-foreground" />
                    <span className="text-foreground">{band?.employeeCount}</span>
                  </div>
                </td>
                <td className="p-4 text-foreground">{band?.effectiveDate}</td>
                <td className="p-4">
                  {getStatusBadge(band?.status)}
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e?.stopPropagation();
                        onEditBand(band);
                      }}
                    >
                      <Icon name="Edit" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e?.stopPropagation();
                        onDeleteBand(band);
                      }}
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4 p-4">
        {sortedBands?.map((band) => (
          <div 
            key={band?.id}
            className={`bg-card border border-border rounded-lg p-4 cursor-pointer transition-smooth ${
              selectedBand?.id === band?.id ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground/20'
            }`}
            onClick={() => onSelectBand(band)}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-medium text-foreground">{band?.name}</h3>
                <p className="text-sm text-muted-foreground">{band?.department} • {band?.jobLevel}</p>
              </div>
              {getStatusBadge(band?.status)}
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('payBandsPage.table2.salaryRange')}</span>
                <span className="text-sm font-medium text-foreground">
                  {formatCurrency(band?.minSalary, band?.currency)} - {formatCurrency(band?.maxSalary, band?.currency)}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('payBandsPage.table2.employees')}</span>
                <div className="flex items-center space-x-1">
                  <Icon name="Users" size={14} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">{band?.employeeCount}</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('payBandsPage.table2.effectiveDate')}</span>
                <span className="text-sm font-medium text-foreground">{band?.effectiveDate}</span>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-2 mt-4 pt-3 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                iconName="Edit"
                iconPosition="left"
                onClick={(e) => {
                  e?.stopPropagation();
                  onEditBand(band);
                }}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="Trash2"
                iconPosition="left"
                onClick={(e) => {
                  e?.stopPropagation();
                  onDeleteBand(band);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
      {sortedBands?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Layers" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t('payBandsPage.table2.noBandsTitle')}</h3>
          <p className="text-muted-foreground">Create your first pay band to get started with salary management.</p>
        </div>
      )}
    </div>
  );
};

export default BandTable;